import { useRef, useEffect, useCallback } from 'react';

const PROFILES = [
  { label: '720p', width: 1280, height: 720,  bitrate: 1500000 },
  { label: '480p', width: 854,  height: 480,  bitrate: 700000  },
  { label: '360p', width: 640,  height: 360,  bitrate: 350000  },
  { label: '240p', width: 426,  height: 240,  bitrate: 150000  },
];

export function useAdaptiveQuality({ streamRef, peersRef, enabled = true, onQualityChange }) {
  const currentIdx = useRef(0);
  const intervalRef = useRef(null);
  const stableCount = useRef(0);

  const applyProfile = useCallback(async (idx) => {
    const profile = PROFILES[idx];
    const stream  = streamRef.current;
    if (!stream) return;

    const vTrack = stream.getVideoTracks()[0];
    if (!vTrack) return;

    try {
      await vTrack.applyConstraints({
        width:  { ideal: profile.width },
        height: { ideal: profile.height },
      });
    } catch {}

    // Appliquer le bitrate sur tous les pairs
    const peers = Object.values(peersRef.current || {});
    for (const pc of peers) {
      const sender = pc.getSenders().find(s => s.track?.kind === 'video');
      if (!sender) continue;
      try {
        const params = sender.getParameters();
        if (!params.encodings?.length) params.encodings = [{}];
        params.encodings[0].maxBitrate = profile.bitrate;
        await sender.setParameters(params);
      } catch {}
    }

    currentIdx.current = idx;
    onQualityChange?.(profile.label);
  }, [streamRef, peersRef, onQualityChange]);

  const checkAndAdapt = useCallback(() => {
    const peers = Object.values(peersRef.current || {});
    if (!peers.length) return;

    let totalRtt = 0, count = 0;
    const promises = peers.map(async (pc) => {
      try {
        const stats = await pc.getStats();
        stats.forEach(s => {
          if (s.type === 'candidate-pair' && s.state === 'succeeded' && s.currentRoundTripTime) {
            totalRtt += s.currentRoundTripTime;
            count++;
          }
        });
      } catch {}
    });

    Promise.all(promises).then(() => {
      if (!count) return;
      const avgRtt = totalRtt / count;
      const idx    = currentIdx.current;

      if (avgRtt > 0.3 && idx < PROFILES.length - 1) {
        stableCount.current = 0;
        applyProfile(idx + 1); // Dégrader
      } else if (avgRtt < 0.1 && idx > 0) {
        stableCount.current++;
        if (stableCount.current >= 3) { // 3 mesures stables avant d'améliorer
          stableCount.current = 0;
          applyProfile(idx - 1); // Améliorer
        }
      }
    });
  }, [peersRef, applyProfile]);

  useEffect(() => {
    if (!enabled) return;
    intervalRef.current = setInterval(checkAndAdapt, 5000);
    return () => clearInterval(intervalRef.current);
  }, [enabled, checkAndAdapt]);

  return { currentProfile: PROFILES[currentIdx.current] };
}
